import React, { useState } from "react";
import * as finService from "../../services/finService";
import useFinOptions from "./useFinOptions";
import { fmtMoney } from "./gl/glContext";
import { DataTable, act, downloadCsv, hint } from "./gl/glUi";
import { EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, formatDate, formatDateTime, pageStyle, useLoad } from "../../components/ui";

// Tax Returns (UAE VAT 201): prepare a return for a period (every tax line not yet in a return,
// late documents included), check the boxes against the ledger's VAT accounts, file it.
const BOXES = [
  ["1a", "Standard rated supplies – Abu Dhabi"],
  ["1b", "Standard rated supplies – Dubai"],
  ["1c", "Standard rated supplies – Sharjah"],
  ["1d", "Standard rated supplies – Ajman"],
  ["1e", "Standard rated supplies – Umm Al Quwain"],
  ["1f", "Standard rated supplies – Ras Al Khaimah"],
  ["1g", "Standard rated supplies – Fujairah"],
  ["3", "Supplies subject to the reverse charge"],
  ["4", "Zero rated supplies"],
  ["5", "Exempt supplies"],
  ["9", "Standard rated expenses"],
  ["10", "Supplies subject to the reverse charge (input)"],
];

function ReturnView({ id, onHide, onChanged }) {
  const { data: r, loading, error, reload } = useLoad(() => finService.getVatReturn(id), [id]);
  const [filing, setFiling] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  const exportLines = () =>
    downloadCsv(
      `vat-${String(r.period_from).slice(0, 10)}.csv`,
      ["Direction", "Box", "Document", "Date", "Party", "TRN", "Tax code", "Taxable", "VAT", "Recoverable"],
      r.lines.map((l) => [l.direction, l.return_box, l.document_number, String(l.document_date).slice(0, 10), l.party_name, l.party_trn, l.tax_rate_code, l.taxable_amount, l.tax_amount, l.recoverable_tax])
    );
  return (
    <SlidePanel show onHide={onHide} title={r ? `VAT return ${formatDate(r.period_from)} – ${formatDate(r.period_to)}` : "VAT return"} width={980}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {r ? (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              {r.status === "DRAFT" ? <PrimaryButton onClick={() => setFiling(true)}>File</PrimaryButton> : null}
              {r.status === "DRAFT" ? <SecondaryButton onClick={() => window.confirm("Delete this draft return?") && act(() => finService.vatReturnAction(id, "delete")).then(() => {
                    onChanged();
                    onHide();
                  })}>Delete draft</SecondaryButton> : null}
              <SecondaryButton onClick={exportLines}>Export lines</SecondaryButton>
              <span style={hint}>
                {r.legal_entity_name} · {r.status === "FILED" ? `filed ${formatDateTime(r.filed_at)}, ref ${r.filing_reference}` : "draft"}
              </span>
            </div>
            <DataTable
              rows={BOXES.map(([box, label]) => ({ box, label, ...(r.boxes[box] || { taxable: 0, tax: 0 }) }))}
              rowKey={(b) => b.box}
              columns={[
                { label: "Box", render: (b) => b.box },
                { label: "", render: (b) => b.label },
                { label: "Amount", num: true, render: (b) => fmtMoney(b.taxable) },
                { label: "VAT", num: true, render: (b) => fmtMoney(b.tax) },
              ]}
            />
            <div className="mt-3" style={{ maxWidth: 460, marginLeft: "auto", fontSize: 14 }}>
              {[
                ["Output VAT due", r.output_tax, r.ledger_check.output_tax],
                ["Recoverable input VAT", r.input_tax, r.ledger_check.input_tax],
                ["Payable (refundable if negative)", r.net_payable, null],
              ].map(([k, v, g]) => (
                <div key={k} className="d-flex justify-content-between">
                  <span>{k}</span>
                  <span>
                    <b>{fmtMoney(v)}</b>
                    {g !== null ? <span style={{ ...hint, marginLeft: 8, color: Number(g) !== Number(v) ? "#B91C1C" : undefined }}>ledger {fmtMoney(g)}</span> : null}
                  </span>
                </div>
              ))}
              {Number(r.ledger_check.output_difference) || Number(r.ledger_check.input_difference) ? (
                <div style={{ ...hint, color: "#B91C1C", marginTop: 6 }}>The ledger's VAT accounts differ from the return (late or manual entries): check before filing.</div>
              ) : null}
            </div>
            <h6 className="mt-4">Lines ({r.lines.length})</h6>
            <DataTable
              rows={r.lines}
              columns={[
                { label: "Box", render: (l) => l.return_box },
                { label: "Document", render: (l) => l.document_number },
                { label: "Date", render: (l) => formatDate(l.document_date) },
                { label: "Party", render: (l) => l.party_name || "" },
                { label: "Taxable", num: true, render: (l) => fmtMoney(l.taxable_amount) },
                { label: "VAT", num: true, render: (l) => fmtMoney(l.direction === "INPUT" ? l.recoverable_tax : l.tax_amount) },
              ]}
            />
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={filing}
        title="File the return"
        onHide={() => setFiling(false)}
        onDone={refresh}
        onSave={(clean) => finService.vatReturnAction(id, "file", clean)}
        fields={[{ key: "filing_reference", label: "Tax authority acknowledgement / reference", required: true, max: 60 }]}
      />
    </SlidePanel>
  );
}

export default function TaxReturns() {
  const { data, loading, error, reload } = useLoad(() => finService.listVatReturns(), []);
  const fin = useFinOptions(["legal-entities"]);
  const [open, setOpen] = useState(null);
  const [preparing, setPreparing] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Tax Returns" subtitle="VAT returns prepared from the documents, checked against the ledger" actions={<PrimaryButton onClick={() => setPreparing(true)}>Prepare return</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No returns yet" />}
          columns={[
            { label: "Legal entity", render: (r) => r.legal_entity_name },
            { label: "Period", render: (r) => `${formatDate(r.period_from)} – ${formatDate(r.period_to)}` },
            { label: "Output", num: true, render: (r) => fmtMoney(r.output_tax) },
            { label: "Input", num: true, render: (r) => fmtMoney(r.input_tax) },
            { label: "Payable", num: true, render: (r) => fmtMoney(r.net_payable) },
            { label: "Status", render: (r) => (r.status === "FILED" ? `Filed (${r.filing_reference})` : "Draft") },
          ]}
        />
      </LoadState>
      {open ? <ReturnView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={preparing}
        title="Prepare a VAT return"
        onHide={() => setPreparing(false)}
        onDone={(data) => {
          reload();
          if (data && data.id) setOpen(data.id);
        }}
        onSave={(clean) => finService.prepareVatReturn(clean)}
        fields={[
          { key: "legal_entity_id", label: "Legal entity", type: "select", required: true, options: fin["legal-entities"] || [] },
          { key: "period_from", label: "From", type: "date", required: true },
          { key: "period_to", label: "To", type: "date", required: true },
        ]}
      />
    </div>
  );
}
